import React, {useMemo} from "react";
import {DatePicker, RangePickerPreset} from "peggirkit";
import {nl} from "date-fns/locale/nl";
import {DateRange} from "react-day-picker";
import {endOfMonth, endOfWeek, startOfDay, startOfMonth, startOfWeek, startOfYesterday, subMonths} from "date-fns";
import {useIntl} from "react-intl";
import {useTooltip} from "@widgets/tooltip/TooltipProvider";
import {SetStateFn} from "@/utils/SetStateUtils";
import {formatPeriod, ReportPeriod} from "@widgets/reports/dayReport/period/ReportPeriod";

type Props = {
    period: ReportPeriod,
    setPeriod: (period: ReportPeriod) => void,
    showDatePicker: boolean,
    setShowDatePicker: SetStateFn<boolean>,
};

/** Weeks start on Monday. */
const weekOptions = {weekStartsOn: 1} as const;

/**
 * Picks the period of the report: one day or several, with presets for
 * today, yesterday, this week, this month and last month.
 */
const DayReportPeriodPicker = ({period, setPeriod, showDatePicker, setShowDatePicker}: Props) => {
    const intl = useIntl();
    const {applyTooltip} = useTooltip();
    const presets: RangePickerPreset[] = useMemo(() => {
        const today: Date = startOfDay(new Date());
        const lastMonth: Date = subMonths(today, 1);
        const ranges: { name: string, range: ReportPeriod }[] = [
            {name: intl.formatMessage({id: "today"}), range: {from: today, to: today}},
            {name: intl.formatMessage({id: "yesterday"}), range: {from: startOfYesterday(), to: startOfYesterday()}},
            {
                name: intl.formatMessage({id: "dayReport.period.thisWeek"}),
                range: {from: startOfWeek(today, weekOptions), to: startOfDay(endOfWeek(today, weekOptions))},
            },
            {
                name: intl.formatMessage({id: "dayReport.period.thisMonth"}),
                range: {from: startOfMonth(today), to: startOfDay(endOfMonth(today))},
            },
            {
                name: intl.formatMessage({id: "dayReport.period.lastMonth"}),
                range: {from: startOfMonth(lastMonth), to: startOfDay(endOfMonth(lastMonth))},
            },
        ];

        return ranges.map(r => ({
            name: r.name,
            range: r.range,
            buttonProps: {
                props: {
                    ...applyTooltip(formatPeriod(r.range)),
                },
            },
        }));
    }, [intl, applyTooltip]);

    return (
        <DatePicker
            title={intl.formatMessage({id: "pickARange"})}
            cancelButton={{text: intl.formatMessage({id: "cancel"})}}
            continueButton={{text: intl.formatMessage({id: "apply"})}}
            show={showDatePicker}
            setShow={setShowDatePicker}
            calendarLocale={nl}
            horizontalViewBreakpoint={"xl"}
            withTimeConfig={{enabled: false}}
            pickerConfig={{
                type: "range",
                defaultRange: period,
                onContinue: (selected: DateRange | undefined) => {
                    if (!selected?.from)
                        return;

                    // One clicked day: only that day
                    setPeriod({from: startOfDay(selected.from), to: startOfDay(selected.to ?? selected.from)});
                },
                presets,
            }}
        />
    );
};

export default DayReportPeriodPicker;